import React, { useState, useEffect, useRef } from 'react';
import { Order, Warehouse, ShippingCarrier, ShipmentItem, Shipment, Package } from '../types';
import api from '../utils/api';
import { hasAddressConflict } from '../utils/addressUtils';
import { useToast } from '../context/ToastContext';
import { countries } from '../utils/countries';
import { getRates, getLabel, validateHTS, WarehouseDetails, ShippingDetails, Rate } from '../utils/freightcomApi';
import { getFdcShippingMethods, sendToFdc, createShipmentRecord, updateOrderStatus, addOrderNote, calculateShippedQuantities, ShippingMethod } from '../utils/fdcApi';
import { getCarrierClass as getFdcCarrierClass } from '../utils/carrierUtils';
import '../OrderDetailsDialog.css';

const syncWithWooCommerce = async (
  order: Order,
  updates: {
    status?: string;
    notes?: { content: string; added_by: string; isCustomerNote?: boolean }[];
    tracking_numbers?: { tracking_number: string; carrier: string; date_shipped: string; delete?: boolean }[];
  }
) => {
  try {
    console.log(`Initiating WooCommerce sync for order ${order.id} with updates:`, updates);
    const response = await api.post('/sync-woocommerce-order', {
      order_id: order.id,
      updates,
    });
    console.log(`WooCommerce sync response for order ${order.id}:`, response.data);
    return response.data;
  } catch (err: any) {
    const errorMessage = err.response?.data?.error || 'Failed to sync with WooCommerce';
    console.error('Sync WooCommerce error:', err);
    throw new Error(errorMessage);
  }
};

interface ShippingDialogProps {
  order: Order;
  selectedItems: string[];
  onClose: () => void;
  refreshOrders: () => Promise<void>;
  warehouseId: number;
  warehouses: Warehouse[];
}

const ShippingDialog: React.FC<ShippingDialogProps> = ({
  order,
  selectedItems,
  onClose,
  refreshOrders,
  warehouseId,
  warehouses,
}) => {
  const { showToast } = useToast();
  const [labelUrl, setLabelUrl] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [shipmentReference, setShipmentReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errorResults, setErrorResults] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [rates, setRates] = useState<Rate[]>([]);
  const [selectedRate, setSelectedRate] = useState<string | null>(null);
  const [isSignatureRequired, setIsSignatureRequired] = useState(false);
  const [isGuaranteedRates, setIsGuaranteedRates] = useState(true);
  const [chargeAccount, setChargeAccount] = useState(true);
  const [orderNote, setOrderNote] = useState('');
  const [warehouseDetails, setWarehouseDetails] = useState<WarehouseDetails | null>(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [showManualLabel, setShowManualLabel] = useState(false);
  const [labelResponse, setLabelResponse] = useState<{ labelUrl: string; trackingNumber: string; carrier: string } | null>(null);
  const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [hasShownValueWarning, setHasShownValueWarning] = useState(false);

  // 3PL specific state
  const [itemValues, setItemValues] = useState<{ [key: string]: string }>({});
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  const [pendingDelete, setPendingDelete] = useState<{order_id: number, shipment_id: number} | null>(null);

  // HTS Selection Modal state
  const [showHTSModal, setShowHTSModal] = useState(false);
  const [htsRecommendations, setHtsRecommendations] = useState<any[]>([]);
  const [selectedHTS, setSelectedHTS] = useState<{[key: number]: {code: string, description: string}}>({});

  // 3PL shipping details
  const [threePLShippingDetails, setThreePLShippingDetails] = useState({
    reference: '',
    shipToName: order.customer_name || '',
    shipToCompany: order.shipping_company || '',
    shipToAddress1: order.shipping_address || '',
    shipToAddress2: order.shipping_address_2 || '',
    shipToCity: order.shipping_city || '',
    shipToState: order.shipping_state || '',
    shipToZip: order.shipping_zipcode || '',
    shipToCountry: order.shipping_country || 'CA',
    shipToPhone: order.customer_phone || '',
    shipToEmail: order.customer_email || '',
  });
  const [shippingDetails, setShippingDetails] = useState<ShippingDetails>({
    name: order.customer_name || '',
    company: order.shipping_company || '',
    country: order.shipping_country || 'CA',
    address1: order.shipping_address || '',
    address2: order.shipping_address_2 || '',
    zipCode: order.shipping_zipcode || '',
    city: order.shipping_city || '',
    state: order.shipping_state || '',
    phone: order.customer_phone || '',
    email: order.customer_email || '',
  });
  const dialogRef = useRef<HTMLDivElement>(null);

  const warehouse = warehouses.find(w => w.id === warehouseId);
  const isFdcWarehouse = warehouse?.name?.toLowerCase().includes('fdc') || false;
  const isThreePLMode = isFdcWarehouse;


  const [hasFetchedData, setHasFetchedData] = useState(false);

  const requiredFields = ['name', 'address1', 'city', 'state', 'country', 'zipCode', 'phone', 'email'] as const;
  const isFormValid = requiredFields.every(field => shippingDetails[field].trim() !== '');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showHTSModal) return;
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, showHTSModal]);

  // Freightcom needs a phone number. When the order has none, fill in the
  // default from Admin > Settings. Only fills a still-empty field, so it never
  // overwrites something typed while the request was in flight.
  useEffect(() => {
    if (order.customer_phone?.trim()) return;
    let cancelled = false;
    api.get('/settings?key=default_shipment_phone')
      .then((response) => {
        const phone = (response.data?.default_shipment_phone || '').trim();
        if (cancelled || !phone) return;
        setShippingDetails((prev) => (prev.phone.trim() === '' ? { ...prev, phone } : prev));
      })
      .catch((err) => console.error('Default shipment phone fetch error:', err));
    return () => { cancelled = true; };
  }, [order.id, order.customer_phone]);

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetchedData || !order.id || selectedItems.length === 0) {
        console.log('Skipping fetchData:', { hasFetchedData, orderId: order.id, selectedItems });
        return;
      }

      try {
        const itemIds = selectedItems.map((key) => Number(key.split('-')[0])).filter(id => !isNaN(id));
        if (itemIds.length === 0) {
          console.log('No valid item IDs in selectedItems:', selectedItems);
          setError('Invalid item selection');
          setTimeout(() => setError(null), 5000);
          return;
        }

        console.log('Fetching data with:', { orderId: order.id, itemIds, warehouseId });
        const [warehouseResponse, packageResponse, shipmentsResponse, carriersResponse] = await Promise.all([
          api.get(`/warehouse-details?warehouse_id=${warehouseId}`),
          api.get(`/order-items-details?order_id=${order.id}&item_ids=${itemIds.join(',')}`),
          api.get(`/shipments-query?order_id=${order.id}`),
          api.get('/shipping-carriers'),
        ]);

        const warehouse = warehouseResponse.data;
        setWarehouseDetails(warehouse);

        const shipmentCount = shipmentsResponse.data.shipments.length + 1;
        const newShipmentReference = `${warehouse.ship_ref_append}${order.id}-${shipmentCount}`;
        if (!shipmentReference) {
          setShipmentReference(newShipmentReference);
          setReferenceNumber(newShipmentReference);
          if (isThreePLMode) {
            setOrderNote(
              `Thank you so much for your order! It is being packaged and will ship shortly with reference ${newShipmentReference}, we will follow up with a tracking number shortly!`
            );
          } else {
            setOrderNote(
              `Thank you so much for your order! It has shipped with reference ${newShipmentReference}, and tracking has been added! (Carrier and tracking to be updated)`
            );
          }
        }
        setShipments(shipmentsResponse.data.shipments || []);

        const itemIdCounts: { [key: number]: number } = {};
        selectedItems.forEach((instanceKey) => {
          const [itemId] = instanceKey.split('-').map(Number);
          itemIdCounts[itemId] = (itemIdCounts[itemId] || 0) + 1;
        });
        const items = packageResponse.data.items.filter((item: any) => itemIdCounts[item.id]).map((item: any) => ({
          ...item,
          quantity: itemIdCounts[item.id],
        }));

        const newPackages = items.map((item: any) => ({
          length: Math.floor(item.length || 0),
          width: Math.floor(item.width || 0),
          height: Math.floor(item.height || 0),
          weight: Math.floor(item.weight || 0),
          numberOfPackages: item.quantity,
          commodity: {
            description: item.commodity_description || '',
            hsCode: item.hs_code || '',
            manufactureCountry: item.manufacture_country || '',
            value: Math.floor(item.commodity_value || 0),
            quantity: item.quantity || 1,
          },
        }));
        setPackages(newPackages);

        const totalValue = newPackages.reduce((sum: number, pkg: Package) => sum + pkg.commodity.value * pkg.commodity.quantity, 0);
        if (totalValue > 750 && !hasShownValueWarning) {
          showToast('Shipment value exceeds $750. Just saying.', 'warning');
          setHasShownValueWarning(true);
        }

        setCarriers(carriersResponse.data || []);

        if (isThreePLMode) {
          try {
            const shipmentsResponse = await api.get(`/shipments-query?order_id=${order.id}`);
            setShipments(shipmentsResponse.data.shipments || []);

            const shipmentCount = shipmentsResponse.data.shipments.length + 1;
            const newReference = `FDC-${order.id}-${shipmentCount}`;
            setThreePLShippingDetails(prev => ({ ...prev, reference: newReference }));
            setReferenceNumber(newReference);
            setOrderNote(
              `Thank you so much for your order! We've finished processing it and tracking will be added shortly!`
            );

            const methodsResponse = await getFdcShippingMethods();
            setShippingMethods(methodsResponse);

            const initialValues: { [key: string]: string } = {};
            selectedItems.forEach(itemKey => {
              const [itemId] = itemKey.split('-').map(Number);
              const orderItem = order.items.find(item => item.id === itemId);
              if (orderItem) {
                initialValues[itemKey] = orderItem.price.toString();
              }
            });
            setItemValues(initialValues);
          } catch (fdcError) {
            console.error('Failed to fetch FDC data:', fdcError);
            setError('Failed to load FDC data');
          }
        }

        console.log('fetchData success:', { packages: newPackages, shipments: shipmentsResponse.data.shipments });
        setHasFetchedData(true);
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Failed to load order details';
        console.error('fetchData error:', err, err.response?.data);
        setError(errorMessage);
        showToast(errorMessage, 'error');
        setTimeout(() => setError(null), 5000);
        setHasFetchedData(true);
      }
    };

    fetchData();
  }, [order.id, selectedItems, warehouseId, showToast, hasFetchedData, hasShownValueWarning, shipmentReference, isThreePLMode, order.items]);

  const handleApiError = (err: any, defaultMessage: string) => {
    let errorMessage = defaultMessage;
    let results = null;
    if (err.cause?.response?.data?.results?.[0]?.message) {
      errorMessage = err.cause.response.data.results[0].message;
      results = err.cause.response.data.results;
    } else if (err.response?.data?.shipnerd_error) {
      errorMessage = err.response.data.shipnerd_error;
      results = err.response.data.results || null;
    } else if (err.response?.data?.error) {
      errorMessage = err.response.data.error;
      results = err.response.data.results || null;
    }
    setError(errorMessage);
    setErrorResults(results);
    showToast(errorMessage, 'error');
    setTimeout(() => {
      setError(null);
      setErrorResults(null);
    }, 5000);
  };

  const handleShippingDetailsChange = (field: keyof ShippingDetails, value: string) => {
    setShippingDetails(prev => ({ ...prev, [field]: value }));
    if (!showManualLabel && !isLocked) {
      if (isThreePLMode) {
        setOrderNote(`Thank you so much for your order! It is being packaged and will ship shortly with reference ${shipmentReference}, we will follow up with a tracking number shortly!`);
      } else {
        setOrderNote(`Thank you so much for your order! It has shipped with reference ${shipmentReference}, and tracking has been added! (Carrier and tracking to be updated)`);
      }
    }
  };

  const handlePackageChange = (index: number, field: keyof Package, value: string | number) => {
    const newPackages = [...packages];
    if (field === 'length' || field === 'width' || field === 'height' || field === 'weight' || field === 'numberOfPackages') {
      const intValue = Math.floor(Number(value) || 0);
      newPackages[index][field] = intValue;
    }
    setPackages(newPackages);
    if (!showManualLabel && !isLocked) {
      if (isThreePLMode) {
        setOrderNote(`Thank you so much for your order! It is being packaged and will ship shortly with reference ${shipmentReference}, we will follow up with a tracking number shortly!`);
      } else {
        setOrderNote(`Thank you so much for your order! It has shipped with reference ${shipmentReference}, and tracking has been added! (Carrier and tracking to be updated)`);
      }
    }
  };

  const handleValueOverride = (index: number, field: keyof Package['commodity'], value: string | number) => {
    const newPackages = [...packages];
    if (field === 'value' || field === 'quantity') {
      newPackages[index].commodity[field] = Math.floor(Number(value) || 0);
    } else {
      (newPackages[index].commodity as any)[field] = typeof value === 'string' ? value : String(value);
    }
    if (field === 'hsCode' || field === 'description') {
      newPackages[index].commodity.hsVerified = false;
      newPackages[index].commodity.providedHsCode = undefined;
      newPackages[index].commodity.providedDescription = undefined;
    }
    setPackages(newPackages);
    if (!showManualLabel && !isLocked) {
      if (isThreePLMode) {
        setOrderNote(`Thank you so much for your order! It is being packaged and will ship shortly with reference ${shipmentReference}, we will follow up with a tracking number shortly!`);
      } else {
        setOrderNote(`Thank you so much for your order! It has shipped with reference ${shipmentReference}, and tracking has been added! (Carrier and tracking to be updated)`);
      }
    }
  };

  const handleReferenceNumberChange = (value: string) => {
    setReferenceNumber(value);
    setShipmentReference(value);
    if (!showManualLabel && !isLocked) {
      if (isThreePLMode) {
        setOrderNote(`Thank you so much for your order! It is being packaged and will ship shortly with reference ${value}, we will follow up with a tracking number shortly!`);
      } else {
        setOrderNote(`Thank you so much for your order! It has shipped with reference ${value}, and tracking has been added! (Carrier and tracking to be updated)`);
      }
    }
  };

  const handleAddPackage = () => {
    setPackages([...packages, {
      length: 0, width: 0, height: 0, weight: 0, numberOfPackages: 1,
      commodity: { description: '', hsCode: '', manufactureCountry: '', value: 0, quantity: 1 },
    }]);
    if (!showManualLabel && !isLocked) {
      setOrderNote(`Thank you so much for your order! It has shipped with reference ${shipmentReference}, and tracking has been added! (Carrier and tracking to be updated)`);
    }
  };

  const handleRemovePackage = (index: number) => {
    setPackages(packages.filter((_, i) => i !== index));
    if (!showManualLabel && !isLocked) {
      setOrderNote(`Thank you so much for your order! It has shipped with reference ${shipmentReference}, and tracking has been added! (Carrier and tracking to be updated)`);
    }
  };

  const handleHTSSelection = () => {
    const updatedPackages = packages.map((pkg, i) => {
      const selected = selectedHTS[i];
      if (selected) {
        return {
          ...pkg,
          commodity: {
            ...pkg.commodity,
            hsCode: selected.code,
            description: selected.description,
            hsVerified: true,
            providedHsCode: pkg.commodity.hsCode,
            providedDescription: pkg.commodity.description,
          },
        };
      }
      return pkg;
    });
    setPackages(updatedPackages);
    setShowHTSModal(false);
    setSelectedHTS({});
    showToast('HTS codes validated. Press "Get Rates" to fetch guaranteed DDP rates.', 'success');
  };

  const handleGetRates = async () => {
    if (!warehouseDetails) { setError('Warehouse details not loaded'); showToast('Warehouse details not loaded', 'error'); return; }
    if (!isFormValid) { setError('Please fill in all required fields'); showToast('Please fill in all required fields', 'error'); setTimeout(() => setError(null), 5000); return; }
    const isDomestic = warehouseDetails.ship_from_country === shippingDetails.country;
    if (!isDomestic && packages.some(pkg => !pkg.commodity.hsCode?.trim())) {
      setError('HS Code is required for all products in international shipments');
      showToast('HS Code is required for all products in international shipments', 'error');
      setTimeout(() => setError(null), 5000);
      return;
    }
    const fromCountry = warehouseDetails.ship_from_country?.toUpperCase().trim();
    const toCountry = shippingDetails.country?.toUpperCase().trim();
    const isCAtoUS = (fromCountry === 'CA' || fromCountry === 'CAN' || fromCountry === 'CANADA') &&
                     (toCountry === 'US' || toCountry === 'USA' || toCountry === 'UNITED STATES');
    const needsValidation = isGuaranteedRates && isCAtoUS && packages.some(pkg => !pkg.commodity.hsVerified);
    if (needsValidation) {
      try {
        const recommendations = await validateHTS(shippingDetails, packages, warehouseDetails, referenceNumber, isGuaranteedRates);
        if (!recommendations || recommendations.length === 0 || recommendations.some((rec: any) => !rec?.recommended_array?.length)) {
          showToast('No HTS recommendations available. Please check product descriptions.', 'error');
          return;
        }
        setHtsRecommendations(recommendations);
        setShowHTSModal(true);
        return;
      } catch (err: any) {
        handleApiError(err, 'HTS validation failed');
        return;
      }
    }
    setLoading(true);
    setLabelResponse(null);
    try {
      const ratesData = await getRates(shippingDetails, packages, warehouseDetails, referenceNumber, isGuaranteedRates);
      setRates(ratesData);
      showToast('Rates fetched successfully', 'success');
    } catch (err: any) {
      handleApiError(err, 'Failed to fetch rates');
    } finally {
      setLoading(false);
    }
  };

  const handleGetLabel = async () => {
    if (!selectedRate) { setError('Please select a rate'); showToast('Please select a rate', 'error'); setTimeout(() => setError(null), 5000); return; }
    if (!warehouseDetails) { setError('Warehouse details not loaded'); showToast('Warehouse details not loaded', 'error'); return; }
    if (!shipmentReference.trim()) { setError('Shipment reference is required'); showToast('Shipment reference is required', 'error'); setTimeout(() => setError(null), 5000); return; }
    setLoading(true);
    setIsLocked(true);
    try {
      const selectedRateObj = rates.find(rate => rate.rateId === selectedRate);
      if (!selectedRateObj) throw new Error('Selected rate not found');
      const labelData = await getLabel(selectedRateObj, shippingDetails, packages, warehouseDetails, shipmentReference, order.id, warehouseId, isGuaranteedRates);
      setLabelResponse(labelData);
      setOrderNote(`Thank you so much for your order! It has shipped with reference ${shipmentReference}, and tracking has been added! ${labelData.carrier} ${labelData.trackingNumber}`);
      showToast('Label retrieved successfully', 'success');
    } catch (err: any) {
      handleApiError(err, 'Failed to create label');
      setIsLocked(false);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleManualLabel = () => {
    setShowManualLabel(!showManualLabel);
    if (!showManualLabel) {
      setRates([]); setSelectedRate(null); setLabelResponse(null);
      setLabelUrl(''); setTrackingNumber(''); setCarrier(''); setOrderNote(''); setIsLocked(false);
    } else {
      if (isThreePLMode) {
        setOrderNote(`Thank you so much for your order! It is being packaged and will ship shortly with reference ${shipmentReference}, we will follow up with a tracking number shortly!`);
      } else {
        setOrderNote(`Thank you so much for your order! It has shipped with reference ${shipmentReference}, and tracking has been added! (Carrier and tracking to be updated)`);
      }
    }
  };

  const handleManualLabelChange = (field: 'labelUrl' | 'trackingNumber' | 'carrier' | 'shipmentReference', value: string) => {
    if (field === 'labelUrl') setLabelUrl(value);
    if (field === 'trackingNumber') setTrackingNumber(value);
    if (field === 'carrier') setCarrier(value);
    if (field === 'shipmentReference') setShipmentReference(value);
    const allFieldsFilled = field === 'labelUrl' ? value && trackingNumber && carrier && shipmentReference :
                           field === 'trackingNumber' ? labelUrl && value && carrier && shipmentReference :
                           field === 'carrier' ? labelUrl && trackingNumber && value && shipmentReference :
                           labelUrl && trackingNumber && carrier && value;
    if (allFieldsFilled) {
      setOrderNote(`Thank you so much for your order! It has shipped with reference ${field === 'shipmentReference' ? value : shipmentReference}, and tracking has been added! ${carrier} ${trackingNumber}`);
    }
  };

  const handleSendToWarehouse = async () => {
    if (!labelResponse && (!labelUrl || !trackingNumber || !carrier || !shipmentReference)) {
      setError('Please provide a valid label URL, tracking number, carrier, and shipment reference');
      showToast('Please provide a valid label URL, tracking number, carrier, and shipment reference', 'error');
      setTimeout(() => setError(null), 5000);
      return;
    }
    setLoading(true);
    try {
      const finalLabelUrl = labelResponse ? labelResponse.labelUrl : labelUrl;
      const finalTrackingNumber = labelResponse ? labelResponse.trackingNumber : trackingNumber;
      const finalCarrier = labelResponse ? labelResponse.carrier : carrier;
      const finalOrderNote = `Thank you so much for your order! It has shipped with reference ${shipmentReference}, and tracking has been added! ${finalCarrier} ${finalTrackingNumber}`;

      const items: ShipmentItem[] = [];
      const itemInstanceCounts: { [key: number]: number } = {};
      Object.entries(itemIdCounts).forEach(([itemId, totalQuantity]) => {
        const item = order.items.find(i => i.id === Number(itemId));
        if (!item) { console.warn(`Item ${itemId} not found in order.items`); return; }
        itemInstanceCounts[Number(itemId)] = itemInstanceCounts[Number(itemId)] || 0;
        for (let i = 0; i < totalQuantity; i++) {
          itemInstanceCounts[Number(itemId)]++;
          items.push({
            id: 0, shipment_id: 0, order_item_id: Number(itemId), quantity: 1,
            product_id: item.product_id, product_name: item.product_name,
            sku: item.sku || null, gtin: item.gtin || null,
            status: 'Pending', scan_timestamp: null, scanned_by: null,
            instance_number: itemInstanceCounts[Number(itemId)],
          });
        }
      });

      showToast('Creating shipment...', 'syncing');
      const shipmentResponse = await api.post('/shipments', {
        action: 'create', order_id: order.id,
        data: { warehouse_id: warehouseId, tracking_number: finalTrackingNumber, carrier: finalCarrier, date_shipped: new Date().toISOString(), shipping_label: finalLabelUrl, shipment_reference: shipmentReference, items },
      });

      showToast('Sending tracking to WooCommerce...', 'syncing');
      await syncWithWooCommerce(order, { tracking_numbers: [{ tracking_number: finalTrackingNumber, carrier: finalCarrier, date_shipped: new Date().toISOString() }] });

      showToast('Adding order note...', 'syncing');
      await api.post('/add-order-note', { order_id: order.id, note: finalOrderNote, added_by: 'MPP ERP System', is_customer_note: true });

      const shippedQuantities: { [itemId: number]: number } = {};
      shipments.forEach((s: any) => { s.items.forEach((si: ShipmentItem) => { shippedQuantities[si.order_item_id] = (shippedQuantities[si.order_item_id] || 0) + 1; }); });
      items.forEach((item) => { shippedQuantities[item.order_item_id] = (shippedQuantities[item.order_item_id] || 0) + 1; });

      const allItemsShipped = order.items.every((item) => (shippedQuantities[item.id] || 0) >= item.quantity);
      const newStatus = allItemsShipped ? 'completed' : 'partial-shipped';
      await api.post('/update-order-status', { order_id: order.id, status: newStatus });

      showToast('Sending order details to WooCommerce...', 'syncing');
      await syncWithWooCommerce(order, { status: newStatus, notes: [{ content: finalOrderNote, added_by: 'MPP ERP System', isCustomerNote: true }] });

      await refreshOrders();
      showToast(`Order sent to ${warehouseDetails?.name} and store updated successfully`, 'success');
      onClose();
    } catch (err: any) {
      handleApiError(err, 'Failed to send to warehouse or update store');
    } finally {
      setLoading(false);
    }
  };

  const handleThreePLSubmit = async () => {
    if (!selectedMethod) { setError('Please select a shipping method'); return; }
    setIsSubmitting(true);
    setError(null);
    try {
      const itemGroups: { [sku: string]: { quantity: number; declaredValue: string } } = {};
      for (const itemKey of selectedItems) {
        const [itemId] = itemKey.split('-').map(Number);
        const orderItem = order.items.find(item => item.id === itemId);
        if (!orderItem?.sku) {
          setError(`Invalid SKU for item ${itemId}. Please ensure all products have valid SKUs.`);
          showToast(`Invalid SKU for item ${itemId}. Please ensure all products have valid SKUs before sending to FDC.`, 'error');
          setIsSubmitting(false);
          return;
        }
        const sku = orderItem.sku;
        const declaredValue = (parseFloat(itemValues[itemKey] || '0')).toFixed(2);
        if (itemGroups[sku]) { itemGroups[sku].quantity += 1; } else { itemGroups[sku] = { quantity: 1, declaredValue }; }
      }
      const items = Object.entries(itemGroups).map(([sku, { quantity, declaredValue }]) => ({ quantity, sku, declaredValue }));
      const shipmentReference = `MPP-${order.id}-${Date.now()}`;
      let instanceCounter = 0;
      const shipmentResponse = await createShipmentRecord(
        order.id, warehouseId, selectedMethod, 'PENDING-3PL',
        new Date().toISOString().split('T')[0], threePLShippingDetails.reference, shipmentReference,
        selectedItems.map(itemKey => {
          const [itemId] = itemKey.split('-').map(Number);
          const orderItem = order.items.find(item => item.id === itemId);
          return { order_item_id: itemId, product_id: orderItem?.product_id || 0, product_name: orderItem?.product_name || '', sku: orderItem?.sku || '', gtin: orderItem?.gtin || '', instance_number: ++instanceCounter };
        })
      );
      const createdShipmentId = shipmentResponse.data.shipment.id;
      await api.post('/shipments', { action: 'update_reference', order_id: order.id, shipment_id: createdShipmentId, reference: threePLShippingDetails.reference });

      let apiResponse: any;
      try {
        const payload = {
          merchantOrderId: threePLShippingDetails.reference, shippingMethod: selectedMethod,
          recipient: {
            companyName: threePLShippingDetails.shipToCompany, country: threePLShippingDetails.shipToCountry,
            postalCode: threePLShippingDetails.shipToZip, addressRegion: threePLShippingDetails.shipToState,
            addressLocality: threePLShippingDetails.shipToCity, address2: threePLShippingDetails.shipToAddress2,
            address1: threePLShippingDetails.shipToAddress1, phone: threePLShippingDetails.shipToPhone,
            lastName: threePLShippingDetails.shipToName?.split(' ').slice(-1)[0] || '',
            firstName: threePLShippingDetails.shipToName?.split(' ')[0] || '',
            email: threePLShippingDetails.shipToEmail,
          },
          items, notes: `Order ${order.id}`,
        };
        apiResponse = await sendToFdc(payload);
      } catch (fdcError) {
        const error = fdcError as any;
        if (error.response?.status === 409 && error.response.data.error.includes('Duplicate Order')) {
          showToast('Duplicate FDC Order Number. Change Reference Number', 'error');
          try { await api.post('/shipments', { action: 'delete', order_id: order.id, shipment_id: createdShipmentId }); } catch {}
        } else {
          try { await api.post('/shipments', { action: 'delete', order_id: order.id, shipment_id: createdShipmentId }); } catch {}
          throw fdcError;
        }
      }

      await api.post('/shipments', { action: 'update_meta', order_id: order.id, shipment_id: createdShipmentId, fdcOrderId: apiResponse.data.fdcOrderId });

      setTimeout(async () => {
        try {
          const statusResponse = await api.post('/fdc/get-order-status', { id: apiResponse.data.fdcOrderId });
          const { reason, stageName, stateName } = statusResponse.data;
          showToast(`Shipment ${createdShipmentId}: FDC Status: ${reason} | Stage: ${stageName} | State: ${stateName}`, 'warning', 10000);
        } catch { showToast('Failed to retrieve FDC order status', 'error'); }
      }, 5000);

      if (apiResponse?.status === 201) {
        const shippedQuantities = calculateShippedQuantities(shipments, selectedItems.reduce((acc, itemKey) => { const [itemId] = itemKey.split('-').map(Number); acc[itemId] = (acc[itemId] || 0) + 1; return acc; }, {} as { [key: number]: number }), order.id);
        const newStatus = 'partial-shipped';
        await updateOrderStatus(order.id, newStatus);
        try { await addOrderNote(order.id, orderNote, 'MPP ERP System', true); } catch (noteError: any) { showToast('Failed to add order note to database', 'error'); }
        try { await syncWithWooCommerce(order, { status: newStatus, notes: [{ content: orderNote, added_by: 'MPP ERP System', isCustomerNote: true }] }); } catch (syncError: any) { showToast('Failed to sync order note with WooCommerce', 'error'); }
      }

      showToast(`Order sent to ${warehouse?.name} successfully`, 'success');
      await refreshOrders();
      onClose();
    } catch (err: any) {
      console.error('3PL submission error:', err);
      setError(err.response?.data?.error || `Failed to send order to ${warehouse?.name}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const itemIdCounts: { [key: number]: number } = {};
  selectedItems.forEach((instanceKey) => {
    const [itemId] = instanceKey.split('-').map(Number);
    itemIdCounts[itemId] = (itemIdCounts[itemId] || 0) + 1;
  });

  // ─────────────────────────────────────────────────────────────
  // Render — NO outer overlay wrapper. ShippingDialog renders as
  // a nested dialog inside OrderDetailsDialog's .order-dialog div.
  // The customer-note-dialog-overlay pattern from OrderDetailsDialog.css
  // is used so it sits on top of the parent dialog panel only.
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="shipping-dialog-overlay">
      <div className="order-dialog" ref={dialogRef}>
        <button className="close-button" onClick={onClose}>✕</button>
        <div className="order-dialog-content">
          <div className="working-area">
            <h3 className="order-title">Ship Order #{order.id}</h3>
            <div className="warehouse-info">From: {warehouseDetails?.name}</div>
            {isThreePLMode ? (
              <>
                <div className="shipping-details">
                  <h4>Shipping Details</h4>
                  <div className="details-row">
                    <div className="detail-column">
                      <label>Name *</label>
                      <input type="text" value={threePLShippingDetails.shipToName} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToName: e.target.value }))} className={threePLShippingDetails.shipToName.trim() === '' ? 'invalid' : ''} />
                    </div>
                    <div className="detail-column">
                      <label>Company</label>
                      <input type="text" value={threePLShippingDetails.shipToCompany} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToCompany: e.target.value }))} />
                    </div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column">
                      <label>Address 1 *</label>
                      <input type="text" value={threePLShippingDetails.shipToAddress1} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToAddress1: e.target.value }))} className={threePLShippingDetails.shipToAddress1.trim() === '' ? 'invalid' : ''} />
                    </div>
                    <div className="detail-column">
                      <label>Address 2</label>
                      <input type="text" value={threePLShippingDetails.shipToAddress2} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToAddress2: e.target.value }))} />
                    </div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column">
                      <label>City *</label>
                      <input type="text" value={threePLShippingDetails.shipToCity} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToCity: e.target.value }))} className={threePLShippingDetails.shipToCity.trim() === '' ? 'invalid' : ''} />
                    </div>
                    <div className="detail-column">
                      <label>State *</label>
                      <input type="text" value={threePLShippingDetails.shipToState} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToState: e.target.value }))} className={threePLShippingDetails.shipToState.trim() === '' ? 'invalid' : ''} />
                    </div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column">
                      <label>Country *</label>
                      <input type="text" value={threePLShippingDetails.shipToCountry} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToCountry: e.target.value }))} className={threePLShippingDetails.shipToCountry.trim() === '' ? 'invalid' : ''} />
                    </div>
                    <div className="detail-column">
                      <label>Zip Code *</label>
                      <input type="text" value={threePLShippingDetails.shipToZip} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToZip: e.target.value }))} className={threePLShippingDetails.shipToZip.trim() === '' ? 'invalid' : ''} />
                    </div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column">
                      <label>Phone *</label>
                      <input type="text" value={threePLShippingDetails.shipToPhone} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToPhone: e.target.value }))} className={threePLShippingDetails.shipToPhone.trim() === '' ? 'invalid' : ''} />
                    </div>
                    <div className="detail-column">
                      <label>Email *</label>
                      <input type="text" value={threePLShippingDetails.shipToEmail} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, shipToEmail: e.target.value }))} className={threePLShippingDetails.shipToEmail.trim() === '' ? 'invalid' : ''} />
                    </div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column">
                      <label>Reference Number</label>
                      <input type="text" value={threePLShippingDetails.reference} onChange={(e) => setThreePLShippingDetails(prev => ({ ...prev, reference: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div className="package-details">
                  <h4>FDC Order Items</h4>
                  <ul className="shipment-items-list">
                    {Object.entries(selectedItems.reduce((acc, itemKey) => { const [itemId] = itemKey.split('-').map(Number); if (!acc[itemId]) { acc[itemId] = { itemKey, count: 0 }; } acc[itemId].count++; return acc; }, {} as { [itemId: number]: { itemKey: string; count: number } })).map(([itemIdStr, { itemKey, count }]) => {
                      const itemId = Number(itemIdStr);
                      const orderItem = order.items.find(item => item.id === itemId);
                      return (
                        <li key={itemId} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid var(--color-border)` }}>
                          <span style={{ flex: 1 }}>{orderItem?.product_name} (Qty: {count})</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Value:</label>
                            <input type="number" step="0.01" value={itemValues[itemKey] || ''} onChange={(e) => setItemValues(prev => ({ ...prev, [itemKey]: e.target.value }))} style={{ width: '80px' }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="rates-container">
                  <h4>Shipping Service</h4>
                  <div className="rates-list">
                    {shippingMethods.map(method => (
                      <button key={method.id} className={`rate-button ${getFdcCarrierClass(method.name)} ${selectedMethod === method.method_code ? 'selected' : ''}`} onClick={() => setSelectedMethod(method.method_code)}>
                        <span className="rate-service">{method.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
                {error && <div className="error-message"><p>{error}</p></div>}
              </>
            ) : !showManualLabel ? (
              <>
                <div className="shipping-details">
                  <h4>Shipping Details</h4>
                  <div className="details-row">
                    <div className="detail-column"><label>Name *</label><input value={shippingDetails.name} onChange={(e) => handleShippingDetailsChange('name', e.target.value)} className={shippingDetails.name.trim() === '' ? 'invalid' : ''} disabled={isLocked || loading} /></div>
                    <div className="detail-column"><label>Company</label><input value={shippingDetails.company} onChange={(e) => handleShippingDetailsChange('company', e.target.value)} disabled={isLocked || loading} /></div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column"><label>Address 1 *</label><input value={shippingDetails.address1} onChange={(e) => handleShippingDetailsChange('address1', e.target.value)} className={shippingDetails.address1.trim() === '' ? 'invalid' : ''} disabled={isLocked || loading} /></div>
                    <div className="detail-column"><label>Address 2</label><input value={shippingDetails.address2} onChange={(e) => handleShippingDetailsChange('address2', e.target.value)} disabled={isLocked || loading} /></div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column"><label>City *</label><input value={shippingDetails.city} onChange={(e) => handleShippingDetailsChange('city', e.target.value)} className={shippingDetails.city.trim() === '' ? 'invalid' : ''} disabled={isLocked || loading} /></div>
                    <div className="detail-column"><label>State *</label><input value={shippingDetails.state} onChange={(e) => handleShippingDetailsChange('state', e.target.value)} className={shippingDetails.state.trim() === '' ? 'invalid' : ''} disabled={isLocked || loading} /></div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column">
                      <label>Country *</label>
                      <select value={shippingDetails.country} onChange={(e) => handleShippingDetailsChange('country', e.target.value)} className={shippingDetails.country.trim() === '' ? 'invalid' : ''} disabled={isLocked || loading}>
                        <option value="">Select Country</option>
                        {countries.map((country) => (<option key={country.code} value={country.code}>{country.name}</option>))}
                      </select>
                    </div>
                    <div className="detail-column"><label>Zip Code *</label><input value={shippingDetails.zipCode} onChange={(e) => handleShippingDetailsChange('zipCode', e.target.value)} className={shippingDetails.zipCode.trim() === '' ? 'invalid' : ''} disabled={isLocked || loading} /></div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column"><label>Phone *</label><input value={shippingDetails.phone} onChange={(e) => handleShippingDetailsChange('phone', e.target.value)} className={shippingDetails.phone.trim() === '' ? 'invalid' : ''} disabled={isLocked || loading} /></div>
                    <div className="detail-column"><label>Email *</label><input value={shippingDetails.email} onChange={(e) => handleShippingDetailsChange('email', e.target.value)} className={shippingDetails.email.trim() === '' ? 'invalid' : ''} disabled={isLocked || loading} /></div>
                  </div>
                  <div className="details-row">
                    <div className="detail-column"><label>Reference Number</label><input value={referenceNumber} onChange={(e) => handleReferenceNumberChange(e.target.value)} disabled={isLocked || loading} /></div>
                  </div>
                </div>
                <div className="package-details">
                  <h4>Package Details</h4>
                  {packages.map((pkg, index) => (
                    <div key={index} className="package-item">
                      <div className="package-header">
                        <h5>Package Group {index + 1}</h5>
                        <div className="package-actions">
                          {index > 0 && (<button className="package-action-button remove" onClick={() => handleRemovePackage(index)} title="Remove Package Group" disabled={isLocked || loading}>−</button>)}
                          <button className="package-action-button add" onClick={handleAddPackage} title="Add Package Group" disabled={isLocked || loading}>+</button>
                        </div>
                      </div>
                      <div className="details-row">
                        <div className="detail-column"><label>Length (in)</label><input type="number" step="1" min="0" value={pkg.length} onChange={(e) => handlePackageChange(index, 'length', e.target.value)} disabled={isLocked || loading} /></div>
                        <div className="detail-column"><label>Width (in)</label><input type="number" step="1" min="0" value={pkg.width} onChange={(e) => handlePackageChange(index, 'width', e.target.value)} disabled={isLocked || loading} /></div>
                      </div>
                      <div className="details-row">
                        <div className="detail-column"><label>Height (in)</label><input type="number" step="1" min="0" value={pkg.height} onChange={(e) => handlePackageChange(index, 'height', e.target.value)} disabled={isLocked || loading} /></div>
                        <div className="detail-column"><label>Weight (lbs)</label><input type="number" step="1" min="0" value={pkg.weight} onChange={(e) => handlePackageChange(index, 'weight', e.target.value)} disabled={isLocked || loading} /></div>
                      </div>
                      <div className="details-row">
                        <div className="detail-column"><label>Number of Packages</label><input type="number" step="1" min="1" value={pkg.numberOfPackages} onChange={(e) => handlePackageChange(index, 'numberOfPackages', e.target.value)} disabled={isLocked || loading} /></div>
                      </div>
                      <div className="details-row">
                        <div className="detail-column"><label>Description</label><input value={pkg.commodity.description} onChange={(e) => handleValueOverride(index, 'description', e.target.value)} disabled={isLocked || loading} /></div>
                        <div className="detail-column"><label>HS Code</label><input value={pkg.commodity.hsCode} onChange={(e) => handleValueOverride(index, 'hsCode', e.target.value)} disabled={isLocked || loading} /></div>
                      </div>
                      {pkg.commodity.hsVerified && (
                        <div className="details-row hts-validated-row">
                          <div className="detail-column"><label className="hts-validated-label">✓ Validated HTS Code</label><span className="hts-validated-value">{pkg.commodity.providedHsCode}</span></div>
                          <div className="detail-column"><label className="hts-validated-label">✓ Validated Description</label><span className="hts-validated-value">{pkg.commodity.providedDescription}</span></div>
                        </div>
                      )}
                      <div className="details-row">
                        <div className="detail-column">
                          <label>Manufacture Country</label>
                          <select value={pkg.commodity.manufactureCountry} onChange={(e) => handleValueOverride(index, 'manufactureCountry', e.target.value)} disabled={isLocked || loading}>
                            <option value="">Select Country</option>
                            {countries.map((country) => (<option key={country.code} value={country.code}>{country.name}</option>))}
                          </select>
                        </div>
                        <div className="detail-column"><label>Value</label><input type="number" step="1" min="0" value={pkg.commodity.value} onChange={(e) => handleValueOverride(index, 'value', e.target.value)} disabled={isLocked || loading} /></div>
                      </div>
                      <div className="details-row">
                        <div className="detail-column"><label>Quantity</label><input type="number" step="1" min="1" value={pkg.commodity.quantity} onChange={(e) => handleValueOverride(index, 'quantity', e.target.value)} disabled={isLocked || loading} /></div>
                      </div>
                    </div>
                  ))}
                  <div className="total-value">Total Value: ${packages.reduce((sum, pkg) => sum + pkg.commodity.value * pkg.commodity.quantity, 0)}</div>
                </div>
                <div className="options">
                  <label><input type="checkbox" checked={isGuaranteedRates} onChange={(e) => setIsGuaranteedRates(e.target.checked)} disabled={isLocked || loading} /> Request Guaranteed Rates?</label>
                  <label><input type="checkbox" checked={isSignatureRequired} onChange={(e) => setIsSignatureRequired(e.target.checked)} disabled={isLocked || loading} /> Require Signature?</label>
                  {order.shipping_country !== 'CA' && (<label><input type="checkbox" checked={chargeAccount} onChange={(e) => setChargeAccount(e.target.checked)} disabled={isLocked || loading} /> Bill Duties To Shipper?</label>)}
                </div>
                <button className="action-button" onClick={handleGetRates} disabled={loading || !isFormValid || isLocked}>Get Rates</button>
              </>
            ) : (
              <div className="manual-label-container">
                <h4>Manual Label</h4>
                <div className="details-row"><div className="detail-column"><label>Label URL</label><input type="text" value={labelUrl} onChange={(e) => handleManualLabelChange('labelUrl', e.target.value)} placeholder="Paste label URL here" className="input-label-url" disabled={loading} /></div></div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Carrier</label>
                    <select value={carrier} onChange={(e) => handleManualLabelChange('carrier', e.target.value)} className="carrier-select" disabled={loading}>
                      <option value="">Select Carrier</option>
                      {carriers.map((c) => (<option key={c.name} value={c.name}>{c.label}</option>))}
                    </select>
                  </div>
                </div>
                <div className="details-row"><div className="detail-column"><label>Tracking Number</label><input type="text" value={trackingNumber} onChange={(e) => handleManualLabelChange('trackingNumber', e.target.value)} placeholder="Enter tracking number" className="input-label-url" disabled={loading} /></div></div>
                <div className="details-row"><div className="detail-column"><label>Shipment Reference</label><input type="text" value={shipmentReference} onChange={(e) => { setShipmentReference(e.target.value); handleManualLabelChange('shipmentReference', e.target.value); }} placeholder="Enter shipment reference (e.g., G-12345-1)" className="input-label-url" disabled={loading} /></div></div>
                <button className="action-button cancel-button" onClick={handleToggleManualLabel} disabled={loading}>Cancel Manual Label</button>
              </div>
            )}
            <div className="order-note-container">
              <h4>Order Note</h4>
              <textarea value={orderNote} onChange={(e) => setOrderNote(e.target.value)} className="note-input" disabled={isLocked || loading || isSubmitting} />
            </div>
            {labelResponse && !showManualLabel ? (
              <div className="rates-container">
                <h4>Label Retrieved</h4>
                <p>Success! Carrier: {labelResponse.carrier}, Tracking: {labelResponse.trackingNumber}</p>
                <button className="action-button" onClick={() => window.open(labelResponse.labelUrl, '_blank', 'noopener,noreferrer')} disabled={!labelResponse.labelUrl}>View Label</button>
              </div>
            ) : rates.length > 0 && !showManualLabel ? (
              <div className="rates-container">
                <h4>Available Rates</h4>
                <div className="rates-list">
                  {rates.map((rate) => (
                    <button key={rate.rateId} className={`rate-button ${rate.carrierClass || ''} ${selectedRate === rate.rateId ? 'selected' : ''}`} onClick={() => setSelectedRate(rate.rateId)}>
                      <span className="rate-price">${rate.totalCharge.toFixed(2)}</span>
                      <span className="rate-service">{rate.carrier} {rate.serviceName}</span>
                      <span className="rate-delivery">Est. Delivery: {rate.estimatedDelivery}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {error && (<div className="error-message"><p>{error}</p>{errorResults && (<pre style={{ fontSize: 'var(--text-xs)', overflowX: 'auto' }}>{JSON.stringify(errorResults, null, 2)}</pre>)}</div>)}
          </div>
          <div className="data-area">
            <div className="data-section">
              <h4>Shipment Items</h4>
              <ul className="shipment-items-list">
                {Object.entries(itemIdCounts).map(([itemId, quantity]) => {
                  const item = order.items.find(i => i.id === Number(itemId));
                  return item ? (<li key={itemId}>{quantity}x {item.product_name}</li>) : null;
                })}
              </ul>
            </div>
            <div className="data-section">
              <h4>Shipping Address</h4>
              <div className={hasAddressConflict(order) ? 'address-mismatch' : ''}>
                {order.shipping_company && <p>{order.shipping_company}</p>}
                <p>{order.customer_name}</p>
                <p>{order.shipping_address}</p>
                {order.shipping_address_2 && <p>{order.shipping_address_2}</p>}
                <p>{order.shipping_city}{order.shipping_city && order.shipping_state ? ', ' : ''}{order.shipping_state} {order.shipping_country}</p>
                {order.shipping_zipcode && <p>Zip: {order.shipping_zipcode}</p>}
                {order.customer_phone && <p>Phone: {order.customer_phone}</p>}
                {order.customer_email && <p>Email: {order.customer_email}</p>}
              </div>
            </div>
            <div className="data-section">
              <h4>Billing Address</h4>
              <p>{order.customer_name}</p>
              <p>{order.customer_address}</p>
              {order.customer_address_2 && <p>{order.customer_address_2}</p>}
              <p>{order.customer_city}{order.customer_city && order.customer_state ? ', ' : ''}{order.customer_state} {order.customer_country}</p>
              {order.customer_zipcode && <p>Zip: {order.customer_zipcode}</p>}
              {order.customer_email && <p>Email: {order.customer_email}</p>}
              {order.customer_phone && <p>Phone: {order.customer_phone}</p>}
            </div>
            {!isThreePLMode && (
              <div className="data-section">
                <button className="action-button" onClick={handleToggleManualLabel} disabled={loading || !!labelResponse}>
                  {showManualLabel ? 'Cancel Manual Label' : 'Manually Attach Label'}
                </button>
              </div>
            )}
          </div>
        </div>
        {error && <div className="error-message">{error}</div>}
        <div className="order-dialog-actions">
          {isThreePLMode ? (
            <button className="action-button" onClick={handleThreePLSubmit} disabled={isSubmitting || !selectedMethod}>{isSubmitting ? 'Sending...' : 'Send Order to FDC'}</button>
          ) : (
            <>
              {(labelResponse || (showManualLabel && labelUrl && trackingNumber && carrier && shipmentReference)) && (
                <button className="action-button save-button" onClick={handleSendToWarehouse} disabled={loading}>Send To {warehouseDetails?.name} And Update Store</button>
              )}
              {selectedRate && !showManualLabel && !labelResponse && (
                <button className="action-button save-button" onClick={handleGetLabel} disabled={loading}>Get Label</button>
              )}
            </>
          )}
        </div>

        {/* HTS Selection Modal — nested inside this dialog panel */}
        {showHTSModal && (
          <div className="customer-note-dialog-overlay" onClick={() => setShowHTSModal(false)}>
            <div className="confirm-dialog" style={{ width: '520px', maxHeight: '75vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <h3>Select HTS Codes for Guaranteed DDP Rates</h3>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>
                For CA-US shipments, select the recommended HTS code and description for each product to get guaranteed DDP rates.
              </p>
              {htsRecommendations.map((productRecs, productIndex) => (
                <div key={productIndex} className="hts-product-section">
                  <h4>Product {productIndex + 1}: {packages[productIndex]?.commodity.description}</h4>
                  <div className="hts-options">
                    {productRecs?.recommended_array?.map((rec: any, recIndex: number) => (
                      <div key={recIndex} className="hts-option" onClick={(e) => e.stopPropagation()}>
                        <input type="radio" id={`hts-${productIndex}-${recIndex}`} name={`hts-${productIndex}`} checked={selectedHTS[productIndex]?.code === rec.valid_code}
                          onChange={(e) => { e.stopPropagation(); setSelectedHTS(prev => ({ ...prev, [productIndex]: { code: rec.valid_code, description: rec.valid_description } })); }} />
                        <label htmlFor={`hts-${productIndex}-${recIndex}`}><strong>{rec.valid_code}</strong>: {rec.valid_description}</label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="customer-note-buttons">
                <button className="action-button cancel-button" onClick={(e) => { e.stopPropagation(); setShowHTSModal(false); setSelectedHTS({}); }}>Cancel</button>
                <button className="action-button" onClick={(e) => { e.stopPropagation(); handleHTSSelection(); }} disabled={Object.keys(selectedHTS).length !== htsRecommendations.length}>Use These HTS Codes</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingDialog;